import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  RedoOutlined,
  RocketOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { Card, Col, Progress, Row, Space, Tag, Typography } from 'antd';
import CampaignCreateEdit from './CreateEdit';
import RecipientsModal from './RecipientsModal';
import {
  CrmCampaignCategory,
  CrmCampaignStatus,
  ICrmCampaign,
  getCrmCampaigns,
  pauseCrmCampaign,
  resumeCrmCampaign,
  startCrmCampaign,
} from '../../../http/crm';

const { Text } = Typography;

const CATEGORY_OPTIONS: { value: CrmCampaignCategory; label: string; color: string }[] = [
  { value: 'LEAD', label: 'Captação / Leads', color: 'blue' },
  { value: 'RETENTION', label: 'Retenção', color: 'purple' },
  { value: 'BIRTHDAY', label: 'Aniversário', color: 'pink' },
  { value: 'TREATMENT_RETURN', label: 'Retorno Tratamento', color: 'cyan' },
  { value: 'OFFICE', label: 'Escritório / Interno', color: 'geekblue' },
  { value: 'OTHERS', label: 'Outros', color: 'default' },
];

const STATUS_OPTIONS: { value: CrmCampaignStatus; label: string; color: string }[] = [
  { value: 'DRAFT', label: 'Rascunho', color: 'default' },
  { value: 'RUNNING', label: 'Executando', color: 'processing' },
  { value: 'PAUSED', label: 'Pausada', color: 'warning' },
  { value: 'COMPLETED', label: 'Concluída', color: 'success' },
];

const getCategoryLabel = (cat: CrmCampaignCategory) =>
  CATEGORY_OPTIONS.find((c) => c.value === cat)?.label ?? cat;
const getCategoryColor = (cat: CrmCampaignCategory) =>
  CATEGORY_OPTIONS.find((c) => c.value === cat)?.color ?? 'default';
const getStatusLabel = (st: CrmCampaignStatus) =>
  STATUS_OPTIONS.find((s) => s.value === st)?.label ?? st;
const getStatusColor = (st: CrmCampaignStatus) =>
  STATUS_OPTIONS.find((s) => s.value === st)?.color ?? 'default';

const Campaigns: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState<string>('');
  const [category, setCategory] = useState<CrmCampaignCategory | undefined>(undefined);
  const [status, setStatus] = useState<CrmCampaignStatus | undefined>(undefined);

  const [campaigns, setCampaigns] = useState<ICrmCampaign[]>([]);
  const [campaign, setCampaign] = useState<ICrmCampaign | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [recipientsCampaign, setRecipientsCampaign] = useState<ICrmCampaign | null>(null);
  const [modalRecipients, setModalRecipients] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listCampaigns = useCallback(async () => {
    setLoading(true);
    setCampaigns([]);
    try {
      const response = await getCrmCampaigns(limit, (page - 1) * limit, search, category, status);
      setPages(response.count);
      setCampaigns(response.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar campanhas.',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, category, status, addToast]);

  const handleStartCampaign = useCallback(async (record: ICrmCampaign) => {
    try {
      const res = await startCrmCampaign(record.id);
      addToast({
        type: 'success',
        title: 'Campanha iniciada',
        description: `${res.queued} destinatários enfileirados. ${res.skipped} pulados.`,
      });
      listCampaigns();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao iniciar campanha.',
      });
    }
  }, [addToast, listCampaigns]);

  const handlePauseCampaign = useCallback(async (record: ICrmCampaign) => {
    try {
      await pauseCrmCampaign(record.id);
      addToast({
        type: 'info',
        title: 'Campanha pausada',
        description: 'A campanha foi pausada com sucesso.',
      });
      listCampaigns();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao pausar campanha.',
      });
    }
  }, [addToast, listCampaigns]);

  const handleResumeCampaign = useCallback(async (record: ICrmCampaign) => {
    try {
      await resumeCrmCampaign(record.id);
      addToast({
        type: 'info',
        title: 'Campanha retomada',
        description: 'A campanha foi retomada com sucesso.',
      });
      listCampaigns();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao retomar campanha.',
      });
    }
  }, [addToast, listCampaigns]);

  const renderProgress = (record: ICrmCampaign) => {
    const total = Math.max(record.total_target ?? 0, 1);
    const percent = Math.min(100, Math.round(((record.sent_count ?? 0) / total) * 100));
    const statusColor =
      record.status === 'COMPLETED' ? 'success' : 'active';
    return (
      <div style={{ minWidth: 120 }}>
        <Progress
          percent={percent}
          size="small"
          status={statusColor as any}
          format={() => `${record.sent_count ?? 0}/${total}`}
        />
        <Row gutter={8} style={{ marginTop: 4 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>
              ✅ {record.delivered_count ?? 0}
            </Text>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>
              👁 {record.read_count ?? 0}
            </Text>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>
              ❌ {record.failed_count ?? 0}
            </Text>
          </Col>
        </Row>
      </div>
    );
  };

  useEffect(() => {
    listCampaigns();
  }, [listCampaigns]);

  return (
    <PageLayout>
      <PageIntro
        badge="CRM"
        icon={<RocketOutlined />}
        title="Campanhas de Comunicação"
        description="Crie, gerencie e dispare campanhas de WhatsApp segmentadas. Use filtros avançados para alcançar a audiência certa."
      />
      <MenuOptions
        showInputSearch
        search={search}
        setSearch={setSearch}
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listCampaigns(),
          },
          permissionFind([PermissionActions.crmCampaignsCreate])
            ? {
                label: 'Cadastrar Campanha',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setCampaign(null);
                  setModalCreate(true);
                },
              }
            : null,
          ...(screenFind([Screens.crmTemplatesView])
            ? [
                {
                  label: 'Templates CRM',
                  key: '3',
                  icon: <TagOutlined />,
                  onClick: () => navigate('/crm/templates'),
                },
              ]
            : []),
          ...(screenFind([Screens.crmMessagingView])
            ? [
                {
                  label: 'Inbox / Mensagens',
                  key: '4',
                  icon: <EyeOutlined />,
                  onClick: () => navigate('/crm/messaging'),
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card title="Categoria" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!category}
                  onChange={() => setCategory(undefined)}
                >
                  Todas
                </Tag.CheckableTag>
                {CATEGORY_OPTIONS.map((c) => (
                  <Tag.CheckableTag
                    key={c.value}
                    checked={category === c.value}
                    onChange={() => setCategory(category === c.value ? undefined : c.value)}
                  >
                    {c.label}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Status" size="small" bordered={false} style={{ marginBottom: 12 }}>
              <Space wrap size={[6, 6]}>
                <Tag.CheckableTag
                  checked={!status}
                  onChange={() => setStatus(undefined)}
                >
                  Todos
                </Tag.CheckableTag>
                {STATUS_OPTIONS.map((s) => (
                  <Tag.CheckableTag
                    key={s.value}
                    checked={status === s.value}
                    onChange={() => setStatus(status === s.value ? undefined : s.value)}
                  >
                    {s.label}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Dica" size="small" bordered={false}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Use o Construtor de Audiência para refinar seu público-alvo.
                Sempre calcule a audiência antes de disparar a campanha.
              </Text>
            </Card>
          </Col>
        </Row>
      </PagePanel>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Nome',
              dataIndex: 'name',
              width: 220,
              render: (value: string, record: ICrmCampaign) => (
                <Space direction="vertical" size={2}>
                  <Text strong>{value}</Text>
                  <Tag color={getCategoryColor(record.category)} style={{ margin: 0 }}>
                    {getCategoryLabel(record.category)}
                  </Tag>
                </Space>
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (value: CrmCampaignStatus) => (
                <Tag color={getStatusColor(value)}>
                  {getStatusLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Tipo Mensagem',
              dataIndex: 'message_type',
              width: 110,
              render: (value: string) =>
                value === 'TEMPLATE' ? 'Template WA' : 'Texto livre',
            },
            {
              title: 'Progresso',
              width: 200,
              render: (_: unknown, record: ICrmCampaign) => renderProgress(record),
            },
            {
              title: 'Criado em',
              dataIndex: 'created_at',
              width: 150,
              render: (value: string) =>
                value ? new Date(value).toLocaleDateString('pt-BR') : '-',
            },
            {
              title: 'Opções',
              width: 190,
              render: (_: unknown, record: ICrmCampaign) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.crmCampaignsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setCampaign(record);
                            setModalCreate(true);
                          },
                        }
                      : null,
                    permissionFind([PermissionActions.crmCampaignsSend])
                      ? record.status === 'DRAFT' || record.status === 'PAUSED'
                        ? {
                            label: record.status === 'PAUSED' ? 'Retomar' : 'Iniciar / Disparar',
                            key: '2',
                            icon: <PlayCircleOutlined />,
                            onClick: () =>
                              record.status === 'PAUSED'
                                ? handleResumeCampaign(record)
                                : handleStartCampaign(record),
                          }
                        : record.status === 'RUNNING'
                          ? {
                              label: 'Pausar',
                              key: '3',
                              icon: <PauseCircleOutlined />,
                              onClick: () => handlePauseCampaign(record),
                            }
                          : null
                      : null,
                    {
                      label: 'Ver destinatários',
                      key: '4',
                      icon: <EyeOutlined />,
                      onClick: () => {
                        setRecipientsCampaign(record);
                        setModalRecipients(true);
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={campaigns}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {modalCreate && (
        <CampaignCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          campaign={campaign}
          setCampaigns={setCampaigns}
          onSaved={listCampaigns}
        />
      )}
      {modalRecipients && recipientsCampaign && (
        <RecipientsModal
          open={modalRecipients}
          close={() => setModalRecipients(false)}
          campaign={recipientsCampaign}
        />
      )}
    </PageLayout>
  );
};

export default Campaigns;
